import React, {useCallback, useEffect, useId, useMemo, useRef, useState} from "react";
import {Button, Table} from "antd";
import withAntDPagination from "./higherOrderComponents/withAntDPagination";
import {SMALL_SCREEN_SIZE} from "../App";
import SortIconComponent from "./SortIconComponent";
import {TableFocusContext, useTableFocus} from "./TableFocusContext";
import TabGroupWrap, {TAB_GROUP_FOCUS_CLASS} from "../nav/TabGroupWrap";
import CustomTh from "./CustomTh";

const HeaderCellWrapper = ({
                               children,
                               column,
                               index,
                               sortOrder,
                               onSort,
                               hasSort,
                               hasFilter,
                               filterIcon,
                               tableId
                           }) => {
    const { headerRefs } = useTableFocus();

    useEffect(() => {
        const sorterEls = document.querySelectorAll('.ant-table-column-sorters');
        sorterEls.forEach(el => {
            if (el.hasAttribute('aria-describedby')) {
                el.removeAttribute('aria-describedby');
            }
        });
    }, []);

    const handleKeyDown = (e) => {
        const isSortKey = e.code === 'Enter' || e.code === 'Space';
        if (e.target.closest('input, textarea, [role="checkbox"]')) {
            return;
        }
        if (!isSortKey) return;
        e.preventDefault();
        if (hasSort) {
            onSort(column);
        } else if (hasFilter && column.toggleFilter) {
            column.toggleFilter(true);
        }
    };

    const handleClick = (e) => {
        e.preventDefault();
        if (hasSort) {
            onSort(column);
        } else if (hasFilter && column.toggleFilter) {
            e.stopPropagation();
            column.toggleFilter(true);
        }
    };

    const getSortAttributes = useCallback(() => {
        if (!hasSort) {
            return {};
        }

        let ariaSortLabel = '';
        if (sortOrder === 'ascend') {
            ariaSortLabel = `Currently sorted ascending, click to sort descending`;
        } else if (sortOrder === 'descend') {
            ariaSortLabel = `Currently sorted descending, click to clear sort`;
        } else {
            ariaSortLabel = `Not sorted, click to sort ascending`;
        }

        return {
            'aria-label': `${ariaSortLabel} for ${typeof children === 'string' ? children : 'this column'}`,
            'aria-sort': sortOrder === 'ascend' ? 'ascending' : (sortOrder === 'descend' ? 'descending' : 'none'),
        };
    }, [sortOrder, children, hasSort]);

    const filterContentId = useId();

    const getFilterAttributes = useCallback(()=>{
        if(!hasFilter){
            return {};
        }
        // console.log('column: ',column);
        return {
            'aria-expanded':column.filterDropdownProps.open,
            'aria-label':`${column.title} filter, ${column.filteredValue.length} filter/s selected`,
            'aria-controls':tableId+''+index
        }
    },[]);

    return (
        <Button
            style={{ width: '100%' }}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            className={'flexSpaceBetween flexAlignCentre link-button'}
            ref={(el) => {
                if (el) {
                    headerRefs.current[index] = el;
                }
            }}
            {...getSortAttributes()}
            {...getFilterAttributes()}
        >
            <span style={{ marginRight: 4 }}>{children}</span>
            <div
                className={'ch-table-header-icon'}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    outline: 'none',
                }}
            >
                {hasSort && <SortIconComponent sortOrder={sortOrder} />}
                {hasFilter && typeof filterIcon === 'function' && filterIcon(filterContentId)}
            </div>
        </Button>
    );
};

const enhanceColumnsWithKeyboardNavigation = (columns, sortInfo, onSort, focusedCell, tableId) => {
    // const interactiveColAmount = columns.filter(col => (!!col.sorter || !!col.filterDropdown)).length;
    let interactiveIndex = -1;
    return columns.map((col, index) => {
        const isInteractive = !!col.sorter || !!col.filterDropdown;
        const isFocused = focusedCell?.rowIndex === 0 && focusedCell?.colIndex === index;
        const baseCol = {
            onHeaderCell: () => ({
                className: isFocused ? TAB_GROUP_FOCUS_CLASS : ''
            })
        };

        if (!isInteractive) {
            return {...baseCol, ...col};
        }
        const isActive = sortInfo.columnKey === col.key,
            sortOrder = isActive ? sortInfo.order : null;
        interactiveIndex++;
        const currentInteractiveIndex = interactiveIndex;

        const {filterIcon, filterDropdown, ...rest} = col;

        // This is the correct way to pass the index.
        let updatedCol = {
            ...baseCol,
            ...rest,
            title: (
                <HeaderCellWrapper
                    column={col}
                    index={currentInteractiveIndex}
                    sortOrder={sortOrder}
                    onSort={onSort}
                    tableId={tableId}
                    // totalHeaderCount={interactiveColAmount}
                    hasSort={!!col.sorter}
                    hasFilter={!!col.filterDropdown}
                    filterIcon={filterIcon}
                >
                    {col.title}
                </HeaderCellWrapper>
            ),
            toggleFilter: col.toggleFilter
        };

        if (filterDropdown) {
            const FilterDropdownComponent = filterDropdown;
            updatedCol.filterDropdown = (props) => (
                <FilterDropdownComponent tableId={tableId} {...props} columnIndex={currentInteractiveIndex} />
            );
        }

        if (filterIcon) {
            updatedCol.filterIcon = filterIcon;
        }

        return updatedCol;
    });
}
const CONT_BODY_PADDING = 82,
    SMALL_SCREEN_BODY_PADDING = 32;

const TableAntD = (props) => {
    const {data = [], dynamicWidth = false, className = '', alignSingleItemsRight = false, pagingDependencies, pagingData = null, pagingFunction, pagingParams, columnData = null, loading, noDataMessage = 'No results found', ...rest} = props;

    const [style, setStyle] = useState(props.style?props.style:{});
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [sortInfo, setSortInfo] = useState({ columnKey: null, order: null });
    const headerRefs = useRef([]);
    const tabGroupRef = useRef();
    const tableRef = useRef();
    const [focusedCell, setFocusedCell] = useState({});
    // const [renderCols,setReRenderCols] = useState(null);

    const tabGroupWrapOptions = {
        ref: tabGroupRef,
        targetRef: tableRef,
        onFocusCellChange: newFocusedCell => {
            setFocusedCell(newFocusedCell)
        },
        rowSelector: "tr.ant-table-row, .ant-table-thead > tr",
        cellSelector: ".ant-table-cell, .ant-table-thead button.link-button"
    }

    useEffect(() => {
        requestAnimationFrame(()=>{
            if(selectedIndex !== null){
                headerRefs.current[selectedIndex]?.focus();
                setSelectedIndex(null);
            }
        })
    }, [selectedIndex]);

    const calculateWidth = () => {
        let width;
        const nav = document.getElementById('nav');
        const navWidth = nav ? nav.offsetWidth : 0;

        if (window.innerWidth <= SMALL_SCREEN_SIZE) {
            width = window.innerWidth - SMALL_SCREEN_BODY_PADDING - 3;
        } else {
            width = window.innerWidth - navWidth - CONT_BODY_PADDING - 3;
        }

        setStyle(prev => ({ ...prev, width }));
    };

    const withEmptyAriaLabels = (columns) => {
        return columns.map((column, colIndex) => {
            const newCol = {
                ...column,
                onCell: (record, rowIndex) => {
                    const existingOnCell = column.onCell ? column.onCell(record, rowIndex) : {};
                    return {
                        ...existingOnCell,
                        className: focusedCell?.rowIndex === (rowIndex + 1) && focusedCell?.colIndex === colIndex
                            ? TAB_GROUP_FOCUS_CLASS
                            : '',
                    };
                },
                render: (text, record) => {
                    const rendered = column.render ? column.render(text, record) : text;
                    if (
                        rendered === null ||
                        rendered === undefined ||
                        rendered === '' ||
                        (React.isValidElement(rendered) && rendered.props === undefined)
                    ) {
                        return <span aria-label="No data available"></span>;
                    }
                    return rendered;
                },
            };

            if (column.children) {
                newCol.children = column.children.map((childCol)=>({
                    ...childCol,
                    onCell: (_, rowIndex) => ({
                        className: focusedCell?.rowIndex === (rowIndex + 1) && focusedCell?.colIndex === colIndex
                            ? TAB_GROUP_FOCUS_CLASS
                            : '',
                    }),
                    render: (text, record) => {
                        const rendered = childCol.render ? childCol.render(text, record) : text;

                        if (rendered === null || rendered === undefined || rendered === '' || (React.isValidElement(rendered) && rendered.props === undefined)) {
                            return <span aria-label={'No data available'}></span>
                        }
                        return rendered;
                    }
                }))
            }

            return newCol;
        });
    };

    useEffect(() => {
        if (dynamicWidth) {
            const handleResize = () => {
                requestAnimationFrame(calculateWidth);
            };

            calculateWidth();
            window.addEventListener("resize", handleResize);
            return () => window.removeEventListener("resize", handleResize);
        }
    }, [dynamicWidth]);

    let scroll = {x:true};
    if(props.scroll){
        scroll = {...props.scroll};
        if(!scroll.hasOwnProperty('x')){
            scroll.x = true;
        }
    }

    let commonClass = 'ch-general-table-antd';
    if(className!==''){
        commonClass += ' '+className;
    }
    const onSort = (column) => {
        const columnKey = column?.key ?? 'unknown';
        const newOrder =
            sortInfo.columnKey === columnKey
                ? sortInfo.order === 'ascend'
                    ? 'descend'
                    : sortInfo.order === 'descend'
                        ? null
                        : 'ascend'
                : 'ascend';
        setSortInfo({ columnKey: columnKey, order: newOrder });
    };
    const tableId = useId();

    // const CustomHeaderCell = useCallback((props)=>{
    //     const {children, ...rest} = props;
    //     delete rest['aria-label']; // remove aria-label property for WCAG
    //     return <th {...rest}>{children}</th>;
    // },[sortInfo, columnData]);

    const commonAttributes = {
        className:commonClass,
        role:"table",
        columns: useMemo(() => {
            if (!columnData) return [];
            return enhanceColumnsWithKeyboardNavigation(
                withEmptyAriaLabels(columnData),
                sortInfo,
                onSort,
                focusedCell,
                tableId
            );
        }, [sortInfo, columnData, focusedCell]),
        components:{header:{cell:CustomTh}},
        size:"small",
        fontSize:"small",
        loading:loading,
        scroll:scroll,
        onChange:(pagination, filters, sorter)=>{
            setSortInfo({
                columnKey: sorter?.columnKey,
                order: sorter?.order
            })
        },
        locale:{
            emptyText: (
                <span className="ch-warning-text">{noDataMessage}</span>
            )
        },
        ...rest,
        style:style
    }

    // const reRenderCols = ()=>{
    //     setReRenderCols(new Date().getTime());
    // }

    if (pagingData !== null) {
        if (pagingData.fetchFunction === undefined) {
            console.error('AntD pagingData requires fetchFunction as a parameter');
        } else if (typeof pagingData.fetchFunction !== 'function') {
            console.error('AntD pagingData fetchFunction parameter is not a function');
        }
        if (pagingData.params === undefined) {
            pagingData.params = {};
            console.warn('pagingData params is undefined');
        }
        if (pagingData.params.pageSize === undefined) {
            pagingData.params.pageSize = 10;
        }
        if (pagingData.params.currentPage === undefined) {
            pagingData.params.currentPage = 1;
        }
        const PagedTable = useMemo(() => {
            if (!pagingData) return null;
            return withAntDPagination(Table, pagingData.fetchFunction, {...pagingData.params}, () => {
                tabGroupRef.current?.build();
            });
        }, Array.isArray(pagingDependencies) ? pagingDependencies : []);

        if (columnData === null) {
            console.error('columnData required for TableAntD');
            return null;
        }

        return (
            <TabGroupWrap {...tabGroupWrapOptions}>
                <TableFocusContext.Provider value={{ headerRefs, setSelectedIndex, selectedIndex }}>
                    <PagedTable ref={tableRef} {...commonAttributes}/>
                </TableFocusContext.Provider>
            </TabGroupWrap>
        )
    } else {
        const MemoizedTable = useMemo(() => {
            return React.forwardRef((props, ref) => (
                <div ref={ref}>
                    <Table {...props} />
                </div>
            ));
        }, []);

        useEffect(() => {
            tabGroupRef.current?.build();
        }, []);

        return (
            <TabGroupWrap {...tabGroupWrapOptions}>
                <TableFocusContext.Provider value={{ headerRefs, setSelectedIndex, selectedIndex }}>
                    <MemoizedTable ref={tableRef} role="grid" dataSource={data} pagination={false} {...commonAttributes}/>
                </TableFocusContext.Provider>
            </TabGroupWrap>
        )
    }
}

export default TableAntD;